/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

export interface ApplicationErrorLogEntry {
    id: string;
    message: string;
    occurredAt: string;
    source: string;
}

export interface LogApplicationErrorOptions {
    message: string;
    source: string;
}

export const APPLICATION_ERROR_LOG_STORAGE_KEY = 'application-error-log';
export const APPLICATION_ERROR_LOG_CHANGE_EVENT = 'application-error-log-change';

function notifyApplicationErrorLogChange(): void {
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event(APPLICATION_ERROR_LOG_CHANGE_EVENT));
    }
}

function getErrorLogStorage(): Storage | null {
    if (typeof window === 'undefined') {
        return null;
    }

    return window.localStorage;
}

function isErrorLogEntry(value: unknown): value is ApplicationErrorLogEntry {
    if (!value || typeof value !== 'object') {
        return false;
    }

    const entry = value as Partial<ApplicationErrorLogEntry>;

    return typeof entry.id === 'string' &&
        typeof entry.message === 'string' &&
        typeof entry.occurredAt === 'string' &&
        typeof entry.source === 'string';
}

export function getApplicationErrorLog(storage = getErrorLogStorage()): ApplicationErrorLogEntry[] {
    if (!storage) {
        return [];
    }

    try {
        const storedEntries: unknown = JSON.parse(
            storage.getItem(APPLICATION_ERROR_LOG_STORAGE_KEY) ?? '[]',
        );

        return Array.isArray(storedEntries)
            ? storedEntries.filter(isErrorLogEntry)
            : [];
    } catch {
        return [];
    }
}

export function logApplicationError(
    options: LogApplicationErrorOptions,
    storage = getErrorLogStorage(),
): void {
    logApplicationErrors([options], storage);
}

/**
 * Persists multiple errors in one operation and ignores messages already logged
 * for the same source. This avoids render storms when one API response contains
 * many missing sensor values.
 */
export function logApplicationErrors(
    errors: readonly LogApplicationErrorOptions[],
    storage = getErrorLogStorage(),
): void {
    if (!storage) {
        return;
    }

    try {
        const storedEntries = getApplicationErrorLog(storage);
        const knownErrors = new Set<string>();
        const uniqueStoredEntries = storedEntries.filter(({ message, source }) => {
            const errorKey = `${source}\u0000${message}`;

            if (knownErrors.has(errorKey)) {
                return false;
            }

            knownErrors.add(errorKey);
            return true;
        });
        const occurredAt = new Date().toISOString();
        const newEntries = errors.flatMap(({ message, source }): ApplicationErrorLogEntry[] => {
            const errorKey = `${source}\u0000${message}`;

            if (knownErrors.has(errorKey)) {
                return [];
            }

            knownErrors.add(errorKey);

            return [{
                id: crypto.randomUUID(),
                message,
                occurredAt,
                source,
            }];
        });

        if (newEntries.length === 0 && uniqueStoredEntries.length === storedEntries.length) {
            return;
        }

        storage.setItem(
            APPLICATION_ERROR_LOG_STORAGE_KEY,
            JSON.stringify([...uniqueStoredEntries, ...newEntries]),
        );
        notifyApplicationErrorLogChange();
    } catch {
        // Logging must never alter the primary application flow.
    }
}

export function clearApplicationErrorLog(storage = getErrorLogStorage()): void {
    if (!storage) {
        return;
    }

    try {
        storage.removeItem(APPLICATION_ERROR_LOG_STORAGE_KEY);
        notifyApplicationErrorLogChange();
    } catch {
        // Clearing diagnostic data must not interrupt the debug view.
    }
}
